import { PlayArrow } from "@mui/icons-material";
import { Box, Grid, Typography } from "@mui/material";
import { capitalizeFirstLetter, orderPhotosByView, getMediaItems } from "@/utils/helper";
import type { MediaSection, Photo, Video } from "@/types/common";
import MediaPreViewModal from "@/common/MediaPreviewModal";
import { PHOTO_VIEWS } from "@/utils/constants";
import useMediaPreview from "@/hooks/useMediaPreview";

interface Props {
  media: MediaSection[];
  variant?: "compact" | "detailed";
}

const VIEW_LABELS = ["Front View", "Side View", "Wide View"];

const MediaSectionComponent = ({ media, variant = "detailed" }: Props) => {
  const allMedia = getMediaItems(media);
  const { handleMediaClick, selectedMedia, selectedPhoto, selectedVideo, setSelectedPhoto, setSelectedVideo } = useMediaPreview(allMedia);
  const isCompact = variant === "compact";

  const getLabelForView = (index: number) => {
    return VIEW_LABELS[index] || "Photo";
  };

  return (
    <>
      <Typography
        sx={{
          fontSize: { xs: "3.4vw", sm: 14 },
          fontWeight: 700,
          color: "#191919",
          mb: "3%",
        }}
      >
        Photos & Videos
      </Typography>

      <Box sx={{ mb: "4%" }}>
        {media?.map(({ photos, videos, placeSection }: MediaSection) => {
          const sectionName = capitalizeFirstLetter(placeSection?.section_name);

          const orderedPhotos =
            photos?.length > 0 ? orderPhotosByView(photos, PHOTO_VIEWS) : [];

          return (
            <Box key={sectionName} sx={{ mb: "4%" }}>

              <Box display="flex" gap={isCompact ? 1 : 2} flexWrap="wrap" alignItems="center">
                {/* Photos */}
                {orderedPhotos.length > 0 && (
                  orderedPhotos.map((p: Photo | null, i: number) =>
                    p ? (
                      isCompact ? (
                        <img
                          key={p?.url}
                          src={p?.url}
                          alt={`${sectionName}_${i + 1}`}
                          onClick={() => handleMediaClick("image", p?.url)}
                          style={{
                            width: 50,
                            height: 50,
                            borderRadius: 8,
                            objectFit: "cover",
                            cursor: "pointer",
                            border: "1px solid rgba(0,0,0,0.1)",
                          }}
                        />
                      ) : (
                        <Grid key={p?.url || `empty-photo-${i}`}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: { xs: "11.65vw", sm: 48 },
                                height: { xs: "11.65vw", sm: 48 },
                                mb: "5%",
                                position: "relative",
                              }}
                            >
                              <img
                                src={p?.url}
                                alt={`${sectionName}_${i + 1}`}
                                onClick={() => handleMediaClick("image", p?.url)}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: 8,
                                  objectFit: "cover",
                                  cursor: "pointer",
                                  border: "1px solid #f0f0f0",
                                }}
                              />
                              {p?.is_correct === false && (
                                <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(211, 47, 47, 0.7)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, pointerEvents: "none" }}>
                                  <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: "bold", textAlign: "center", lineHeight: 1 }}>
                                    Not Approved
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <Typography
                              sx={{
                                fontSize: { xs: "3.4vw", sm: 14 },
                                color: "#191919",
                              }}
                            >
                              {getLabelForView(i)}
                            </Typography>
                          </Box>
                        </Grid>
                      )
                    ) : null,
                  )
                )}

                {/* Videos */}
                {videos?.length > 0 && (
                  videos.map((v: Video) =>
                    isCompact ? (
                      <Box
                        key={v?.url}
                        sx={{ position: "relative", cursor: "pointer" }}
                        onClick={() => handleMediaClick("video", v?.url)}
                      >
                        <video
                          src={v?.url}
                          muted
                          preload="metadata"
                          style={{
                            width: 50,
                            height: 50,
                            borderRadius: 8,
                            objectFit: "cover",
                            pointerEvents: "none",
                            backgroundColor: "rgba(0,0,0,0.35)",
                          }}
                        />
                        {v?.is_correct === false && (
                          <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(211, 47, 47, 0.7)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, pointerEvents: "none" }}>
                            <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: "bold", textAlign: "center", lineHeight: 1 }}>
                              Not Approved
                            </Typography>
                          </Box>
                        )}
                        <PlayArrow
                          sx={{
                            position: "absolute",
                            inset: 0,
                            margin: "auto",
                            color: "#fff",
                            zIndex: 2,
                          }}
                        />
                      </Box>
                    ) : (
                      <Grid key={v?.url}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                          }}
                        >
                          <Box
                            sx={{
                              position: "relative",
                              cursor: "pointer",
                              width: { xs: "11.65vw", sm: 48 },
                              height: { xs: "11.65vw", sm: 48 },
                              mb: "8%",
                            }}
                            onClick={() => handleMediaClick("video", v?.url)}
                          >
                            <video
                              src={v?.url}
                              muted
                              preload="metadata"
                              style={{
                                width: "100%",
                                height: "100%",
                                borderRadius: 8,
                                objectFit: "cover",
                                pointerEvents: "none",
                                backgroundColor: "#000",
                              }}
                            />
                            {v?.is_correct === false && (
                              <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(211, 47, 47, 0.7)", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1, pointerEvents: "none" }}>
                                <Typography sx={{ color: "#fff", fontSize: "10px", fontWeight: "bold", textAlign: "center", lineHeight: 1 }}>
                                  Not Approved
                                </Typography>
                              </Box>
                            )}
                            <Box
                              sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "rgba(0,0,0,0.2)",
                                borderRadius: 2,
                              }}
                            >
                              <PlayArrow sx={{ color: "#fff", fontSize: 24 }} />
                            </Box>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: { xs: "3.4vw", sm: 14 },
                              color: "#191919",
                              fontWeight: 500,
                            }}
                          >
                            Video
                          </Typography>
                        </Box>
                      </Grid>
                    ),
                  )
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      <MediaPreViewModal
        selectedPhoto={selectedPhoto}
        selectedVideo={selectedVideo}
        setSelectedPhoto={setSelectedPhoto}
        setSelectedVideo={setSelectedVideo}
        allMedia={selectedMedia}
      />
    </>
  );
};

export default MediaSectionComponent;
